import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import EmptyState from '../../../components/EmptyState';
import LoadOverlay from '../../../components/LoadOverlay';
import LoadScreen from '../../../components/LoadScreen';
import GenericModal from '../../../components/modal/GenericModal';
import WithModals from '../../../components/HOC/withModals';
import Overlay from '../../../components/Overlay';
import StatusTableHeader from '../../../components/StatusTableHeader';
import localeLookup from '../../../config/locale';
import {
  CHAMP_ID,
  DELETED_GROUP_ID,
  DELETED_PERSON_ID,
  EMPTY_ID,
  EXPERT_ID,
  KNOWLEDGE_OWNER_ID,
  MENTOR_ID,
  NONE_ID,
  PERSON_STATES,
  SELF_STUDY_ID,
} from '../../../constants';
import {
  updateTrainingSessionElementsService,
  updateTrainingSessionParticipantsService,
} from '../../../services/trainingSessionService';
import {
  getElementContentIcon,
  getElementStatusContentIcon,
} from '../../../utils/elementHelpers';
import ElementStatusV2Filters from './ElementStatusV2Filters';
import ElementStatusV2Table from './ElementStatusV2Table';
import { filterDefaultState } from './elementStatusV2Utils';
import { useElementStatusV2 } from './useElementStatusV2';
import { queryElementsService } from '../../../services/contentService';

function ElementStatusV2(props) {
  const history = useHistory();
  const location = useLocation();
  const routeStateFilter =
    props?.location?.state?.filterSettings ||
    props?.history?.location?.state?.filterSettings ||
    location?.state?.filterSettings ||
    history?.location?.state?.filterSettings ||
    null;
  const [detailsRow, setDetailsRow] = useState(null);
  const {
    activeFilterId,
    applySavedFilter,
    bulkActionError = null,
    bulkAdminComplete,
    bulkAdminReset,
    bulkChangeResponsible,
    bulkComplete,
    bulkReset,
    clearBulkActionError,
    catalogs = {},
    defaultFilters,
    defaultFilterId,
    filterIds = {},
    filters = filterDefaultState,
    hasError,
    isLoading,
    isLoadingInitial,
    isLoadingMore,
    isSelectingAll = false,
    presetFilters,
    refreshRowsForPersonElements,
    refreshRowStatus,
    rows,
    loadMore,
    selectedCount,
    selectedPersonElements,
    selectedRows,
    setSorting,
    setDefaultFilter,
    setFilters,
    sorting,
    totalElements,
    toggleAllLoadedRows,
    toggleRowSelection,
    updateSavedFilter,
    createSavedFilter,
    deleteSavedFilter,
  } = useElementStatusV2({ initialFilter: routeStateFilter });

  const catalogOrganisationUnits = catalogs.organisationUnits || {};
  const previousBulkActionErrorRef = useRef(null);
  const organisationUnitRootNodes = catalogs.organisationUnitRootNodes || {};
  const catalogRoles = catalogs.roles || {};
  const catalogAreas = catalogs.knowledgeAreas || {};
  const catalogElements = catalogs.knowledgeElements || {};
  const catalogPersons = catalogs.persons || {};
  const catalogCategories = catalogs.categories || {};
  const groups = catalogs.groups || {};
  const wildcardPersonsState = catalogs.wildcardPersons || {};
  const normalizedFilters = {
    ...filterDefaultState,
    ...filters,
    elementConfiguration:
      filters?.elementConfiguration || filterDefaultState.elementConfiguration,
    knowledgeAreas:
      filters?.knowledgeAreas || filterDefaultState.knowledgeAreas,
    knowledgeElements:
      filters?.knowledgeElements || filterDefaultState.knowledgeElements,
    mentors: filters?.mentors || filterDefaultState.mentors,
    organisationUnits:
      filters?.organisationUnits || filterDefaultState.organisationUnits,
    otherFilters: filters?.otherFilters || filterDefaultState.otherFilters,
    persons: filters?.persons || filterDefaultState.persons,
    roles: filters?.roles || filterDefaultState.roles,
    status: filters?.status || filterDefaultState.status,
    trainers: filters?.trainers || filterDefaultState.trainers,
  };

  const peopleLikeCatalog = {
    ...groups,
    ...wildcardPersonsState,
    ...catalogPersons,
  };

  const getLookupTooltip = (id) => {
    const group = groups[id];

    if (group) {
      if ((group.members || []).length === 0) {
        return localeLookup('translations.No persons in group');
      }

      return group.members
        .map((memberId) => {
          const person = catalogPersons[memberId];

          if (!person) {
            return null;
          }

          const personState =
            person.state ||
            (person.isActive === false ? archivedPersonState : '');
          const archivedSuffix =
            personState === archivedPersonState
              ? ` (${localeLookup('translations.Archived')})`
              : '';

          return `${person.name}${archivedSuffix} (${person.initials}${
            person.employeeNumber ? ` · ${person.employeeNumber}` : ''
          })`;
        })
        .filter(Boolean)
        .join('\n');
    }

    const person = catalogPersons[id];

    if (person) {
      return `${person.initials}${
        person.employeeNumber ? ` · ${person.employeeNumber}` : ''
      }`;
    }

    return null;
  };

  const getWildcardInitials = (id) => {
    if (id === DELETED_PERSON_ID) return localeLookup('translations.[DP]');
    if (id === DELETED_GROUP_ID) return localeLookup('translations.[DG]');
    if (id === NONE_ID) return localeLookup('translations.[N]');
    return '';
  };

  const lookupResponsible = (id, { handleEmptyId, isLocked } = {}) => {
    if (id === SELF_STUDY_ID) {
      return {
        id,
        initials: '',
        isGroup: false,
        isWildcardPerson: true,
        name: '',
        showError: false,
        suffix: '',
      };
    }

    if (handleEmptyId && id === EMPTY_ID) {
      if (isLocked) {
        return {
          id,
          initials: '',
          isGroup: false,
          isWildcardPerson: true,
          name: localeLookup('translations.Relevant person'),
          showError: false,
          suffix: '',
        };
      }

      return null;
    }

    const wildcardPerson = wildcardPersonsState[id];

    if (wildcardPerson) {
      return {
        id,
        initials: getWildcardInitials(id),
        isGroup: false,
        isWildcardPerson: true,
        name: wildcardPerson.name,
        showError: id === DELETED_PERSON_ID || id === DELETED_GROUP_ID,
        suffix: '',
      };
    }

    const group = groups[id];

    if (group) {
      return {
        id,
        initials: group.shortName,
        isGroup: true,
        isWildcardPerson: false,
        name: group.name,
        showError: false,
        suffix: '',
        tooltip: getLookupTooltip(id),
      };
    }

    const person = catalogPersons[id];

    if (person) {
      const personState =
        person.state || (person.isActive === false ? archivedPersonState : '');
      const isArchived = personState === archivedPersonState;

      return {
        ...person,
        id,
        isArchived,
        isGroup: false,
        isWildcardPerson: false,
        showError: isArchived,
        suffix: isArchived ? `(${localeLookup('translations.Archived')})` : '',
      };
    }

    return null;
  };

  const archivedPersonState = PERSON_STATES?.ARCHIVED || 'Archived';

  const statusHeaderPersons = Object.keys(catalogPersons).reduce(
    (accumulator, id) => {
      accumulator[id] = {
        ...catalogPersons[id],
        isActive:
          catalogPersons[id]?.isActive ??
          catalogPersons[id]?.state !== archivedPersonState,
      };
      return accumulator;
    },
    {}
  );

  const trainerVisibleItemIds = [
    EMPTY_ID,
    DELETED_PERSON_ID,
    ...Object.keys(wildcardPersonsState).filter((id) =>
      [KNOWLEDGE_OWNER_ID, EXPERT_ID, MENTOR_ID].includes(id)
    ),
    ...Object.keys(peopleLikeCatalog).filter(
      (id) =>
        ![
          KNOWLEDGE_OWNER_ID,
          EXPERT_ID,
          MENTOR_ID,
          SELF_STUDY_ID,
          DELETED_PERSON_ID,
          DELETED_GROUP_ID,
          CHAMP_ID,
        ].includes(id)
    ),
  ];

  const mentorVisibleItemIds = [
    EMPTY_ID,
    NONE_ID,
    DELETED_PERSON_ID,
    DELETED_GROUP_ID,
    ...Object.keys(peopleLikeCatalog).filter(
      (id) =>
        ![EMPTY_ID, NONE_ID, DELETED_PERSON_ID, DELETED_GROUP_ID].includes(id)
    ),
  ];

  const allFilterIds = {
    knowledgeAreas: filterIds.areaIds || [],
    knowledgeElements: filterIds.knowledgeElementIds || [],
    mentors: filterIds.mentorIds || mentorVisibleItemIds,
    organisationUnits: filterIds.organisationUnitIds || [],
    persons: filterIds.personIds || [],
    roles: filterIds.roleIds || [],
    trainers: filterIds.trainerIds || trainerVisibleItemIds,
  };

  const onSubmenuItemToggle = (filterKey, id) => {
    const deselected = normalizedFilters[filterKey]?.deselected || [];
    const nextDeselected = deselected.includes(id)
      ? deselected.filter((itemId) => itemId !== id)
      : [...deselected, id];

    setFilters({
      ...normalizedFilters,
      [filterKey]: {
        ...normalizedFilters[filterKey],
        deselected: nextDeselected,
      },
    });
  };

  const onSubmenuSelectAll = (filterKey) => {
    setFilters({
      ...normalizedFilters,
      [filterKey]: {
        ...normalizedFilters[filterKey],
        deselected: [],
      },
    });
  };

  const onSubmenuDeselectAll = (filterKey, ids) => {
    setFilters({
      ...normalizedFilters,
      [filterKey]: {
        ...normalizedFilters[filterKey],
        deselected: ids,
      },
    });
  };

  const onOrganisationUnitToggle = (id) => {
    onSubmenuItemToggle('organisationUnits', id);
  };

  const onOrganisationUnitToggleMultiple = (ids) => {
    const allSelected = ids.every(
      (id) => !normalizedFilters.organisationUnits.deselected.includes(id)
    );
    const nextDeselected = new Set(
      normalizedFilters.organisationUnits.deselected
    );

    if (allSelected) {
      ids.forEach((id) => nextDeselected.add(id));
    } else {
      ids.forEach((id) => nextDeselected.delete(id));
    }

    setFilters({
      ...normalizedFilters,
      organisationUnits: {
        ...normalizedFilters.organisationUnits,
        deselected: [...nextDeselected],
      },
    });
  };

  const onBulkActionSubmit = ({ action, mediator, note, validFromDate }) => {
    const subtitle = `${
      selectedCount === 1
        ? localeLookup('translations.{0} selected element', [selectedCount])
        : localeLookup('translations.{0} selected elements', [selectedCount])
    }`;

    const body = localeLookup(
      'translations.This change cannot be undone as a bulk action, but can be changed on the individual elements afterwards'
    );

    if (action === 'complete') {
      props.showModal('bulkCompletion', {
        elements: selectedPersonElements,
        fullWidth: true,
        maxWidth: '800px',
        onElementsCompleted: async (personIds) => {
          const completedRows = selectedPersonElements.filter((row) =>
            personIds.includes(row.personId)
          );
          const refreshedRows = await refreshRowsForPersonElements(
            completedRows
          );
          return refreshedRows.map((row) => ({
            elementId: row.elementId,
            elementName: row.elementName,
            mediatorId:
              row.raw?.mediatorId ||
              row.raw?.responsibleId ||
              row.responsibleId,
            nextAction: row.nextAction,
            pending: row.pending,
            personId: row.personId,
          }));
        },
        subtitle,
      });
    }
    if (action === 'mediator') {
      props.showModal('confirmation', {
        title: localeLookup('translations.Change responsible'),
        subtitle,
        infoText: localeLookup(
          'translations.This action will change the responsible for the selected elements'
        ),
        body,
        safeWord: selectedCount >= 10 && localeLookup('translations.Confirm'),
        confirmButtonText: localeLookup('translations.Change'),
        confirmButtonType: 'alert',
        fullWidth: true,
        maxWidth: '500px',
        onConfirm: () => bulkChangeResponsible({ mediatorId: mediator }),
      });
    }

    if (action === 'resetStatus') {
      props.showModal('confirmation', {
        title: localeLookup('translations.Add reset'),
        subtitle,
        infoText: localeLookup(
          "translations.This action adds a reset to the selected elements which you have permission to reset. The elements' history will be preserved"
        ),
        body,
        safeWord: selectedCount >= 10 && localeLookup('translations.Confirm'),
        confirmButtonText: localeLookup('translations.Add'),
        confirmButtonType: 'alert',
        fullWidth: true,
        maxWidth: '500px',
        onConfirm: () => bulkReset({ note }),
      });
    }
    if (action === 'adminResetStatus') {
      props.showModal('confirmation', {
        title: localeLookup('translations.Add reset as admin'),
        subtitle,
        infoText: localeLookup(
          "translations.This action ignores if the elements are locked and adds an admin reset to the selected elements. The elements' history will be preserved"
        ),
        body,
        safeWord: selectedCount >= 10 && localeLookup('translations.Confirm'),
        confirmButtonText: localeLookup('translations.Add'),
        confirmButtonType: 'alert',
        fullWidth: true,
        maxWidth: '500px',
        onConfirm: () => bulkAdminReset({ note, validFromDate }),
      });
    }
    if (action === 'adminApproval') {
      props.showModal('confirmation', {
        title: localeLookup('translations.Complete as admin'),
        subtitle,
        infoText: localeLookup(
          "translations.This action overrides the elements' completion requirements and adds an admin completion to the selected elements"
        ),
        body,
        safeWord: selectedCount >= 10 && localeLookup('translations.Confirm'),
        confirmButtonText: localeLookup('translations.Complete'),
        confirmButtonType: 'alert',
        fullWidth: true,
        maxWidth: '500px',
        onConfirm: () => bulkAdminComplete({ note, validFromDate }),
      });
    }
  };

  const onOpenCreateSavedFilter = () => {
    props.showModal('nameModal', {
      confirmButtonText: localeLookup('translations.Save'),
      defaultValue: '',
      inputPlaceholder: localeLookup('translations.Name'),
      onConfirm: ({ name }) => {
        createSavedFilter({ name });
        props.hideModal();
      },
      subtitle: localeLookup('translations.Saved filters'),
      title: localeLookup('translations.Save current filter'),
    });
  };

  const onOpenRenameSavedFilter = (filter) => {
    props.showModal('nameModal', {
      confirmButtonText: localeLookup('translations.Rename'),
      defaultValue: filter?.name || '',
      inputPlaceholder: localeLookup('translations.Name'),
      onConfirm: ({ name }) => {
        if (filter?.id) {
          updateSavedFilter({ id: filter.id, name });
        }
        props.hideModal();
      },
      subtitle: localeLookup('translations.Saved filters'),
      title: localeLookup('translations.Rename'),
    });
  };

  const onOpenDeleteSavedFilter = (filter) => {
    props.showModal('confirmation', {
      btnRejectTitle: localeLookup('translations.Cancel'),
      confirmButtonText: localeLookup('translations.Delete'),
      onCancel: props.hideModal,
      onConfirm: () => {
        if (filter?.id) {
          deleteSavedFilter(filter.id);
        }
        props.hideModal();
      },
      subtitle: filter?.name || '',
      title: localeLookup('translations.Delete'),
    });
  };

  const onOpenOverwriteSavedFilter = (filter) => {
    props.showModal('confirmation', {
      body: localeLookup(
        'translations.This will overwrite this saved filter with the current filter'
      ),
      btnRejectTitle: localeLookup('translations.Cancel'),
      confirmButtonText: localeLookup('translations.Overwrite'),
      onCancel: props.hideModal,
      onConfirm: () => {
        if (filter?.id) {
          updateSavedFilter({
            id: filter.id,
            name: filter.name,
          });
        }
        props.hideModal();
      },
      subtitle: filter?.name || '',
      title: localeLookup('translations.Overwrite'),
    });
  };

  const onOpenCreateTrainingSession = () => {
    props.showModal('createTrainingSession', {
      fullWidth: true,
      maxWidth: '600px',
      onCreated: onTrainingSessionCreated,
    });
  };

  useEffect(() => {
    if (
      bulkActionError &&
      bulkActionError !== previousBulkActionErrorRef.current
    ) {
      props.showModal('infoModal', {
        title: localeLookup('translations.Bulk action limit exceeded'),
        subtitle: localeLookup('translations.{0} selected elements', [
          totalElements ?? selectedCount,
        ]),
        body: localeLookup(
          'translations.Bulk actions are limited to {0} elements. Try selecting fewer elements at a time to complete them.',
          [5000]
        ),
      });
      clearBulkActionError();
    }

    previousBulkActionErrorRef.current = bulkActionError;
  }, [
    bulkActionError,
    clearBulkActionError,
    props,
    selectedCount,
    totalElements,
  ]);
  const onTrainingSessionCreated = async ({ data: id }) => {
    const selectedPersonIds = Object.keys(selectedRows);
    const uniqueElementIds = [
      ...new Set(
        Object.values(selectedRows).reduce(
          (accumulator, elementIds) => [...accumulator, ...elementIds],
          []
        )
      ),
    ];

    props.hideModal();

    try {
      await Promise.all([
        updateTrainingSessionElementsService(id, uniqueElementIds),
        updateTrainingSessionParticipantsService(id, selectedPersonIds),
      ]);
      props.showModal('confirmation', {
        body: localeLookup(
          'translations.The training session has been created. Do you want to go there now?'
        ),
        confirmButtonText: localeLookup('translations.Go to session'),
        onConfirm: () => onGoToCreatedTrainingSession(id),
        title: localeLookup('translations.Training session created'),
      });
    } catch (error) {
      console.log(error);
    }
  };

  const onGoToCreatedTrainingSession = (id) => {
    history.push({
      pathname: `/training/sessions/${id}`,
      state: {
        backTitle: localeLookup('translations.Element status'),
      },
    });
    props.hideModal();
  };

  const onOpenElementContent = async (row) => {
    const elementResponse = await queryElementsService([row.elementId]);
    const elementData = elementResponse?.data?.[row.elementId] || {};
    props.showModal('richText', {
      fullWidth: true,
      maxWidth: '600px',
      description: elementData.description || '',
      elementId: row.elementId,
      files: elementData.files || [],
      title: row.elementName,
    });
  };

  const onOpenHistory = (row) => {
    props.showModal('elementHistory', {
      cancelButtonText: localeLookup('translations.Close'),
      elementId: row.elementId,
      elementName: row.elementName,
      fullWidth: true,
      maxWidth: '500px',
      onChangeHistory: () => refreshRowStatus(row.personId, row.elementId),
      personId: row.personId,
      subtitle: `${row.personName} · ${row.elementName}`,
      title: localeLookup('translations.History'),
    });
  };
  if (isLoadingInitial) {
    return <LoadScreen />;
  }

  const organisationUnitIds = allFilterIds.organisationUnits || [];
  const dynamicFilterKeys = [
    'roles',
    'knowledgeAreas',
    'knowledgeElements',
    'persons',
    'trainers',
    'mentors',
  ];
  const isAllOrganisationUnitsDeselected =
    normalizedFilters.organisationUnits.deselected.length > 0 &&
    (organisationUnitIds.length === 0
      ? true
      : organisationUnitIds.every((id) =>
          normalizedFilters.organisationUnits.deselected.includes(id)
        ));
  const isAnyDynamicFilterFullyDeselected = dynamicFilterKeys.some((key) => {
    const ids = allFilterIds[key] || [];
    const deselected = normalizedFilters[key]?.deselected || [];

    return ids.length > 0 && ids.every((id) => deselected.includes(id));
  });

  const tableRows = rows.map((row) => {
    const elementCatalogEntry = catalogElements[row.elementId] || {};
    const responsible = lookupResponsible(
      row.raw?.responsibleId || row.responsibleId,
      {
        handleEmptyId: true,
        isLocked: row.raw?.locked,
      }
    );
    const knowledgeAreaId =
      row.areaId ||
      row.raw?.knowledgeAreaId ||
      row.raw?.knowledgeArea?.id ||
      row.raw?.connections?.knowledgeArea ||
      elementCatalogEntry.connections?.knowledgeArea;
    const knowledgeArea = catalogAreas[knowledgeAreaId];
    const isCompleted =
      typeof row.status === 'string' &&
      row.status.startsWith('ValidCompletion');
    const isMissingResponsible = (() => {
      if (!responsible) {
        return false;
      }

      switch (responsible.id) {
        case DELETED_PERSON_ID:
          return true;
        case EXPERT_ID:
          return (row.raw?.responsibleIds || []).includes(EXPERT_ID);
        case KNOWLEDGE_OWNER_ID:
          return (row.raw?.responsibleIds || []).includes(KNOWLEDGE_OWNER_ID);
        case MENTOR_ID:
          return (
            row.raw?.mentorId === NONE_ID || row.raw?.mentorId === EMPTY_ID
          );
        default:
          return responsible.state === archivedPersonState;
      }
    })();

    const responsibleTooltip = (() => {
      const responsibleId = responsible?.id;

      if (responsibleId === EXPERT_ID) {
        if (!knowledgeArea || (knowledgeArea.experts || []).length === 0) {
          return localeLookup('translations.No experts');
        }

        return (knowledgeArea.experts || [])
          .map((id) => catalogPersons[id]?.name)
          .filter(Boolean)
          .join(', ');
      }

      if (responsibleId === KNOWLEDGE_OWNER_ID) {
        return localeLookup('translations.No knowledge owner');
      }

      if (responsible && !responsible.isWildcardPerson) {
        return responsible.isGroup
          ? responsible.tooltip
          : `${responsible.initials}${
              responsible.employeeNumber
                ? ` · ${responsible.employeeNumber}`
                : ''
            }`;
      }

      return null;
    })();
    const responsibleTitle = (() => {
      if (responsible?.id === SELF_STUDY_ID) {
        return row.personName || row.raw?.person?.name || row.responsible;
      }

      return responsible
        ? `${responsible.name} ${responsible.suffix || ''}`.trim()
        : row.responsible;
    })();
    return {
      ...row,
      description: row.description ?? elementCatalogEntry.description,
      elementIcon:
        row.elementIcon ||
        getElementStatusContentIcon({
          contentType: row.contentType,
        }),
      files: row.files ?? elementCatalogEntry.files ?? [],
      responsible: responsibleTitle,
      responsibleTitleColor:
        !isCompleted && isMissingResponsible
          ? 'red'
          : row.responsibleTitleColor,
      responsibleTooltip: responsibleTooltip ?? row.responsibleTooltip,
    };
  });
  return (
    <>
      <div className="status-table" data-testid="element-status-v2-root">
        <ElementStatusV2Filters
          activeFilterId={activeFilterId}
          allFilterIds={allFilterIds}
          catalogAreas={catalogAreas}
          catalogCategories={catalogCategories}
          catalogElements={catalogElements}
          catalogOrganisationUnits={catalogOrganisationUnits}
          catalogPersons={catalogPersons}
          catalogRoles={catalogRoles}
          defaultFilterId={defaultFilterId}
          defaultFilters={defaultFilters}
          filters={normalizedFilters}
          groups={groups}
          onApplySavedFilter={applySavedFilter}
          onCreateSavedFilter={onOpenCreateSavedFilter}
          onDeleteSavedFilter={onOpenDeleteSavedFilter}
          onFiltersChange={setFilters}
          onOrganisationUnitToggle={onOrganisationUnitToggle}
          onOrganisationUnitToggleMultiple={onOrganisationUnitToggleMultiple}
          onOverwriteSavedFilter={onOpenOverwriteSavedFilter}
          onResetFilters={() => setFilters(filterDefaultState)}
          onSetDefaultFilter={setDefaultFilter}
          onSubmenuDeselectAll={onSubmenuDeselectAll}
          onSubmenuItemToggle={onSubmenuItemToggle}
          onSubmenuSelectAll={onSubmenuSelectAll}
          onUpdateSavedFilter={onOpenRenameSavedFilter}
          organisationUnitRootNodes={organisationUnitRootNodes}
          organisationUnits={catalogOrganisationUnits}
          presetFilters={presetFilters}
          wildcardPersons={wildcardPersonsState}
        />
        <div className="status-table__table">
          {(isLoading || isLoadingMore || isSelectingAll) && (
            <LoadOverlay light />
          )}
          {rows.length === 0 ? (
            <div className="status-table__table-no-content">
              <EmptyState
                body={localeLookup(
                  'translations.The selected filters contains no elements'
                )}
                fullHeight
                title={localeLookup('translations.No elements found')}
              />
            </div>
          ) : (
            <>
              <StatusTableHeader
                numSelected={selectedCount}
                onClickCreateTrainingSession={onOpenCreateTrainingSession}
                onSubmit={onBulkActionSubmit}
                persons={statusHeaderPersons}
              />
              {hasError ? (
                <div className="status-table__table-no-content">
                  <EmptyState
                    body={localeLookup('translations.Please try again')}
                    fullHeight
                    title={localeLookup('translations.Something went wrong')}
                  />
                </div>
              ) : isAllOrganisationUnitsDeselected ? (
                <EmptyState
                  body={localeLookup(
                    'translations.Please select organisation units in the filter to view data'
                  )}
                  fullHeight
                  icon="warning"
                  title={localeLookup(
                    'translations.No organisation units selected'
                  )}
                />
              ) : isAnyDynamicFilterFullyDeselected ? (
                <div className="status-table__table-no-content">
                  <EmptyState
                    body={localeLookup(
                      'translations.Please select at least one option in the active filters to view data'
                    )}
                    fullHeight
                    title={localeLookup(
                      'translations.No filter options selected'
                    )}
                  />
                </div>
              ) : (
                <ElementStatusV2Table
                  onLoadMore={loadMore}
                  onOpenDetails={setDetailsRow}
                  onOpenElementContent={onOpenElementContent}
                  onOpenHistory={onOpenHistory}
                  onRefreshRow={refreshRowStatus}
                  onSortChange={(columnId) =>
                    setSorting({
                      columnId,
                      direction:
                        sorting.columnId === columnId &&
                        sorting.direction === 'asc'
                          ? 'desc'
                          : 'asc',
                    })
                  }
                  rows={tableRows}
                  selectedRows={selectedRows}
                  sorting={sorting}
                  toggleAllLoadedRows={toggleAllLoadedRows}
                  toggleRowSelection={toggleRowSelection}
                />
              )}
            </>
          )}
        </div>
      </div>
      <Overlay
        isOpen={Boolean(detailsRow)}
        onClose={() => setDetailsRow(null)}
        subtitle={detailsRow?.personName}
        title={detailsRow?.elementName}
      >
        {detailsRow ? (
          <div data-testid="element-status-v2-details">
            <div>{`${localeLookup('translations.Person')}: ${
              detailsRow.personName || ''
            }`}</div>
            {detailsRow.personSubtitle ? (
              <div>{detailsRow.personSubtitle}</div>
            ) : null}
            <div>{`${localeLookup('translations.Responsible')}: ${
              detailsRow.responsible || ''
            }`}</div>
            {detailsRow.responsibleSubtitle ? (
              <div>{detailsRow.responsibleSubtitle}</div>
            ) : null}
            <div>{`${localeLookup('translations.Mentor')}: ${
              detailsRow.mentor || ''
            }`}</div>
            <div>{`${localeLookup('translations.Latest activity')}: ${
              detailsRow.latestActivity || ''
            }`}</div>
            {detailsRow.latestActivitySubtitle ? (
              <div>{detailsRow.latestActivitySubtitle}</div>
            ) : null}
            <div>{`${localeLookup('translations.Expiration date')}: ${
              detailsRow.expirationDate || ''
            }`}</div>
            <div>{`${localeLookup('translations.Organisation unit')}: ${
              detailsRow.organisationUnit || ''
            }`}</div>
            <div>{`${localeLookup('translations.Role')}: ${
              detailsRow.role || ''
            }`}</div>
            <div>{`${localeLookup('translations.Knowledge area')}: ${
              detailsRow.module || ''
            }`}</div>
          </div>
        ) : null}
      </Overlay>
    </>
  );
}

export default WithModals(ElementStatusV2);
